import { useMemo, useState, type FormEvent } from 'react';
import { suggest } from '@shared/suggestions.ts';
import { normalizeName } from '@shared/text.ts';
import { getStore, useAppState } from '../../data/store.ts';
import { Avatar, ScreenTitle, Stepper } from '../../components/ui.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Link, match, usePath } from '../../lib/router.tsx';
import { useCartPlan, useNow, usePrepTiming } from '../../lib/hooks.ts';
import { OrderSheet } from './OrderSheet.tsx';
import { ItemSheet } from './ItemSheet.tsx';

export function GroceriesScreen() {
  const path = usePath();
  const store = getStore();
  const list = useAppState((s) => s.list);
  const staples = useAppState((s) => s.staples);
  const events = useAppState((s) => s.events);
  const orders = useAppState((s) => s.orders);
  const plan = useCartPlan();
  const now = useNow();
  const timing = usePrepTiming(now);

  const [name, setName] = useState('');
  const [qty, setQty] = useState(1);

  const needed = useMemo(
    () => list.filter((i) => i.status === 'needed').sort((a, b) => a.addedAt.localeCompare(b.addedAt)),
    [list],
  );
  const unmatchedIds = useMemo(() => new Set(plan.unmatched.map((i) => i.id)), [plan]);
  const suggestions = useMemo(() => suggest({ staples, events, list, now, limit: 8 }), [staples, events, list, now]);
  const typed = normalizeName(name);
  const typeahead = useMemo(
    () => (typed.length >= 2 ? staples.filter((s) => normalizeName(s.name).includes(typed) && normalizeName(s.name) !== typed).slice(0, 4) : []),
    [staples, typed],
  );
  const onTheWay = useMemo(() => orders.filter((o) => o.status === 'placed').sort((a, b) => b.placedAt.localeCompare(a.placedAt)), [orders]);
  const ready = plan.stores.filter((s) => s.status === 'ready');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    store.addItem(name, qty);
    setName('');
    setQty(1);
  };

  let prepTitle = 'Carts prepare automatically';
  let prepDetail = `${store.state.settings.quietMinutes} min after the last edit`;
  if (needed.length === 0) {
    prepTitle = 'Nothing on the list';
    prepDetail = 'Add items and carts get prepared for you';
  } else if (!timing.due) {
    prepDetail = `${store.state.settings.quietMinutes} min after the last edit · next in ${timing.minutesLeft} min`;
  } else if (ready.length) {
    prepTitle = ready.length === 1 ? `${ready[0].storeName} cart ready` : `${ready.length} carts ready`;
    prepDetail = ready.map((s) => `${s.lines.length} items, about $${Math.round(s.subtotal + s.fee)}`).join(' · ');
  } else {
    prepTitle = 'Waiting on minimums';
    prepDetail = 'Open order status to see what each store needs';
  }

  const orderOpen = path === '/groceries/order';
  const itemMatch = match('/groceries/item/:id', path);

  return (
    <div className="screen">
      <ScreenTitle
        title="Groceries"
        right={
          <div className="row" style={{ gap: 8 }}>
            <span className="tiny muted">Synced</span>
            <span className="avatar-pair row" style={{ gap: 0 }}>
              <Avatar person="michael" />
              <Avatar person="natasha" />
            </span>
          </div>
        }
      />

      <div className="card row" style={{ padding: '12px 14px 12px 16px' }}>
        <div className="grow stack" style={{ gap: 2 }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{prepTitle}</span>
          <span className="small muted">{prepDetail}</span>
        </div>
        <Link to="/groceries/order" transition="none" className="btn btn-soft" style={{ flex: '0 0 auto' }}>
          Order status
        </Link>
      </div>

      <form className="row" style={{ gap: 8 }} onSubmit={submit}>
        <label htmlFor="add-item" className="sr-only">
          Add an item
        </label>
        <input
          id="add-item"
          className="input grow"
          placeholder="Add an item"
          autoComplete="off"
          enterKeyHint="done"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Stepper value={qty} onChange={(n) => setQty(Math.max(1, n))} label="quantity to add" boxed min={1} />
        <button type="submit" className="icon-btn gold" aria-label="Add item" disabled={!name.trim()}>
          <Icon name="plus" size={22} strokeWidth={2.2} />
        </button>
      </form>

      {typeahead.length > 0 && (
        <div className="chips" aria-label="Matching staples">
          {typeahead.map((s) => (
            <button
              key={s.id}
              type="button"
              className="chip"
              onClick={() => {
                store.addItem(s.name, qty > 1 ? qty : s.usualQty, s.id);
                setName('');
                setQty(1);
              }}
            >
              + {s.name}
            </button>
          ))}
        </div>
      )}

      <div className="stack">
        <span className="small muted" style={{ fontWeight: 600 }}>
          Often added
        </span>
        <div className="chips">
          <button type="button" className="chip chip-gold" onClick={() => store.addUsual()}>
            <Icon name="redo" size={16} />
            Add the usual
          </button>
          {suggestions.map((s) => (
            <button key={s.name} type="button" className="chip" onClick={() => store.addItem(s.name, s.qty, s.stapleId)}>
              {s.due && <span className="dot" aria-label="Due" />}+ {s.name}
            </button>
          ))}
        </div>
      </div>

      <section className="stack" aria-labelledby="needed-h">
        <div className="row-between" style={{ alignItems: 'baseline' }}>
          <h2 className="h2" id="needed-h">
            Needed
          </h2>
          <span className="small muted">
            {needed.length} item{needed.length === 1 ? '' : 's'}
          </span>
        </div>
        {needed.length === 0 ? (
          <div className="empty">The list is empty. Add something above, or tap Add the usual.</div>
        ) : (
          <div className="card-flush">
            {needed.map((i) => (
              <div className="list-row" key={i.id}>
                <input
                  id={`chk-${i.id}`}
                  type="checkbox"
                  className="checkbox"
                  aria-label={`Remove ${i.name} from the list`}
                  onChange={() => store.removeItem(i.id)}
                  checked={false}
                />
                <Link to={`/groceries/item/${i.id}`} transition="none" className="grow stack" style={{ gap: 0, color: 'var(--text)' }}>
                  <span style={{ fontSize: 16 }}>{i.name}</span>
                  {unmatchedIds.has(i.id) && <span className="tiny" style={{ color: 'var(--orange-text)' }}>No store yet · tap to choose</span>}
                  {i.storeOverride && <span className="tiny muted">Set to {store.state.stores.find((s) => s.id === i.storeOverride)?.name}</span>}
                </Link>
                <Avatar person={i.addedBy} small />
                <Stepper value={i.qty} onChange={(n) => store.setQty(i.id, n)} label={`${i.name} quantity`} />
              </div>
            ))}
          </div>
        )}
      </section>

      {onTheWay.length > 0 && (
        <section className="stack" aria-labelledby="otw-h">
          <h2 className="h2" id="otw-h">
            On the way
          </h2>
          {onTheWay.map((o) => {
            const storeName = store.state.stores.find((s) => s.id === o.storeId)?.name ?? o.storeId;
            const count = o.lines.reduce((n, l) => n + l.qty, 0);
            return (
              <div className="card row" key={o.id} style={{ padding: '10px 12px 10px 16px' }}>
                <span style={{ color: 'var(--gold)' }}>
                  <Icon name="truck" size={22} />
                </span>
                <div className="grow stack" style={{ gap: 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 600 }}>
                    {storeName} · {count} item{count === 1 ? '' : 's'}
                  </span>
                  <span className="small muted">{o.lines.map((l) => l.name).join(', ')}</span>
                </div>
                <button type="button" className="btn" onClick={() => store.markDelivered(o.id)}>
                  Arrived
                </button>
              </div>
            );
          })}
        </section>
      )}

      {orderOpen && <OrderSheet />}
      {itemMatch && <ItemSheet itemId={itemMatch.id} />}
    </div>
  );
}
